import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import {
  Ajuda, Girando, MolduraDaEntrada, Notificacao, Seta, Telefone,
} from './MolduraDaEntrada';

/**
 * A entrada pelo link do e-mail.
 *
 * ─── O segredo mora depois do `#`, e isso não é detalhe ──────────────
 *
 * O que vem depois do `#` numa URL é a única parte que o navegador
 * nunca manda ao servidor. Não entra na linha de requisição, não entra
 * no `Referer`, não entra no log do nginx. O código de acesso fica
 * entre o e-mail e este navegador, e em mais lugar nenhum.
 *
 * É também o que faz o link sobreviver ao mundo real. Antivírus de
 * e-mail, o próprio Gmail e o pré-carregamento de link ABREM o endereço
 * sozinhos, antes de qualquer pessoa clicar. Se o segredo viajasse na
 * consulta, esse robô o entregaria ao servidor e um código de uso único
 * morreria antes de chegar ao cliente — que veria «link já usado» no
 * primeiro clique. No fragmento, o robô busca a página e leva nada.
 *
 * ─── E por que ele é apagado do endereço na primeira linha ───────────
 *
 * Porque depois de usado ele não serve para mais nada e só faz mal:
 * fica no histórico do navegador, aparece no endereço se a pessoa
 * mostrar a tela para alguém, e viaja junto se ela copiar o endereço
 * para mandar a um colega. `replaceState` também evita que voltar ou
 * recarregar tente gastar de novo um código que já foi gasto.
 *
 * A leitura acontece no carregamento do módulo, e não dentro de um
 * componente, porque ela tem efeito colateral — e em React um efeito
 * colateral dentro do desenho roda duas vezes no modo estrito.
 */
export const segredoDoLink = (() => {
  const bruto = window.location.hash.replace(/^#/, '');
  if (!bruto) return null;
  const p = new URLSearchParams(bruto);
  const c = p.get('c');
  const t = p.get('t');
  if (!c || !t) return null;
  window.history.replaceState(null, '', window.location.pathname);
  return { c, t };
})();

/**
 * A tela que aparece entre o clique no e-mail e o portal.
 *
 * Ela dura o tempo de uma requisição, e por isso quase não é uma tela:
 * uma linha dizendo o que está acontecendo. O que ela precisa fazer bem
 * é o caso ruim — link velho, link já usado, link de um código que a
 * pessoa pediu de novo. Aí ela não pode deixar o cliente numa tela
 * morta: manda de volta para pedir outro, que é a única saída que
 * existe.
 *
 * Desde a onda 284 ela mora na mesma moldura da entrada — a faixa da
 * marca —, e não numa página solta sem cabeçalho: quem chega pelo link
 * vê a mesma casa de quem pede o código.
 */
export function Acesso(
  { segredo, aoEntrar }: {
    segredo: { c: string; t: string }; aoEntrar: () => void;
  },
) {
  const [erro, setErro] = useState<string | null>(null);
  const jaTentou = useRef(false);

  useEffect(() => {
    /*
     * Uma vez só. No modo estrito o React monta, desmonta e monta de
     * novo — sem esta trava, o segundo POST encontraria o código já
     * gasto pelo primeiro e o cliente veria «link inválido» tendo
     * entrado com sucesso.
     */
    if (jaTentou.current) return;
    jaTentou.current = true;

    void (async () => {
      try {
        await api('/confirmar-link', { metodo: 'POST', corpo: segredo });
        aoEntrar();
      } catch (e) {
        setErro((e as Error)?.message
          ?? 'Este link não vale mais. Peça um novo código.');
      }
    })();
  }, [segredo, aoEntrar]);

  return (
    <MolduraDaEntrada>
      {!erro ? (
        <>
          <h1>Entrando no portal</h1>
          <p className="carregando" role="status">
            <Girando /> Conferindo o link do e-mail…
          </p>
        </>
      ) : (
        <>
          <h1>Entrar no portal</h1>
          <Notificacao tipo="erro" titulo={erro}>
            Links de acesso valem dez minutos e servem uma vez só. Peça
            outro e ele chega no mesmo e-mail.
          </Notificacao>
          <button
            type="button"
            className="botao largo seta"
            /*
             * Recarrega em vez de trocar de estado: a página volta sem
             * fragmento nenhum no endereço e o módulo relê o link — que
             * agora não existe mais —, caindo na tela de pedir código.
             */
            onClick={() => window.location.replace('/entrar')}
          >
            <span>Pedir um novo código</span>
            <Seta />
          </button>
          <Ajuda>Dúvidas? Fale com a Testesolo pelo <Telefone />.</Ajuda>
        </>
      )}
    </MolduraDaEntrada>
  );
}
